import { relations, sql } from 'drizzle-orm'
import { index, pgEnum, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getNullableIdReference,
} from '../common/utils'
import { attachments } from './attachment'
import { customers } from './customer'
import { users } from './user'

// Chat room type enum
export const chatRoomTypeEnum = pgEnum('chat_room_type', [
  'customer_service',
  'group',
])

// Chat room participant role enum
export const chatParticipantRoleEnum = pgEnum('chat_participant_role', [
  'owner',
  'member',
  'support',
])

// Chat rooms table
export const chatRooms = pgTable(
  'chat_rooms',
  {
    ...getBaseFields(),
    name: text().notNull(),
    type: chatRoomTypeEnum().notNull().default('customer_service'),
    customerId: getNullableIdReference(() => customers.id),
  },
  (table) => [
    ...getBaseIndexes('chat_rooms', table),
    uniqueIndex('chat_rooms_customer_id_idx').on(table.customerId),
  ],
)

// Chat room participants
export const chatRoomParticipants = pgTable(
  'chat_room_participants',
  {
    ...getBaseFields(),
    roomId: getIdReference(() => chatRooms.id),
    userId: getIdReference(() => users.id),
    role: chatParticipantRoleEnum().notNull().default('member'),
  },
  (table) => [
    ...getBaseIndexes('chat_room_participants', table),
    uniqueIndex('chat_room_participants_room_user_idx')
      .on(table.roomId, table.userId)
      .where(sql`${table.deletedAt} IS NULL`),
    index('chat_room_participants_user_id_idx').on(table.userId),
  ],
)

// Chat messages
export const chatMessages = pgTable(
  'chat_messages',
  {
    ...getBaseFields(),
    roomId: getIdReference(() => chatRooms.id),
    senderId: getIdReference(() => users.id),
    content: text().notNull(),
  },
  (table) => [
    ...getBaseIndexes('chat_messages', table),
    index('chat_messages_room_id_idx').on(table.roomId),
  ],
)

// Chat read markers - tracks reading progress
export const chatReadMarkers = pgTable(
  'chat_read_markers',
  {
    ...getBaseFields(),
    messageId: getIdReference(() => chatMessages.id),
    userId: getIdReference(() => users.id),
  },
  (table) => [
    ...getBaseIndexes('chat_read_markers', table),
    index('chat_read_markers_user_id_idx').on(table.userId),
  ],
)

// Relations
export const chatRoomsRelations = relations(chatRooms, ({ one, many }) => ({
  customer: one(customers, {
    fields: [chatRooms.customerId],
    references: [customers.id],
  }),
  participants: many(chatRoomParticipants),
  messages: many(chatMessages),
}))

export const chatRoomParticipantsRelations = relations(
  chatRoomParticipants,
  ({ one }) => ({
    room: one(chatRooms, {
      fields: [chatRoomParticipants.roomId],
      references: [chatRooms.id],
    }),
    user: one(users, {
      fields: [chatRoomParticipants.userId],
      references: [users.id],
    }),
  }),
)

export const chatMessagesRelations = relations(
  chatMessages,
  ({ one, many }) => ({
    room: one(chatRooms, {
      fields: [chatMessages.roomId],
      references: [chatRooms.id],
    }),
    sender: one(users, {
      fields: [chatMessages.senderId],
      references: [users.id],
    }),
    attachments: many(attachments),
    readMarkers: many(chatReadMarkers),
  }),
)

export const chatReadMarkersRelations = relations(
  chatReadMarkers,
  ({ one }) => ({
    message: one(chatMessages, {
      fields: [chatReadMarkers.messageId],
      references: [chatMessages.id],
    }),
    user: one(users, {
      fields: [chatReadMarkers.userId],
      references: [users.id],
    }),
  }),
)
